'use client';

import { useMemo } from 'react';
import { timeToTimeslot } from '@/shared/lib/timeslot/timeslot.types';
import { usePlannerSchedules } from '@/entities/planner/model/usePlannerSchedules';
import { useTopLevelRegion } from '@/entities/service-region/model/useTopLevelRegion';
import type { TimeTableSlot, TimeTableSlotStatus } from './visit-counsel-schedule.types';

interface IProps {
  serviceRegionId: string;
  date: string;
  restrictToCustomerTimeslots?: boolean;
}

export interface UseTimeTableDataResult {
  slots: TimeTableSlot[];
  loading: boolean;
  error: Error | null;
}

/**
 * 타임테이블 초기 데이터를 생성하는 훅
 */
export function useTimeTableData(options: IProps): UseTimeTableDataResult {
  const { serviceRegionId, date, restrictToCustomerTimeslots = false } = options;

  const {
    topLevelRegionId,
    loading: topLevelRegionLoading,
    error: topLevelRegionError,
  } = useTopLevelRegion(serviceRegionId || undefined, !!serviceRegionId);

  const {
    schedules,
    loading: schedulesLoading,
    error: schedulesError,
  } = usePlannerSchedules({
    date: date || undefined,
    regionId: topLevelRegionId || undefined,
    enabled: !!topLevelRegionId && !!date,
    useCheckpoint: false,
  });

  const loading = topLevelRegionLoading || schedulesLoading;
  const error = topLevelRegionError || schedulesError;

  const slots = useMemo(() => {
    if (!date || !topLevelRegionId || schedules.length === 0) {
      return [];
    }

    const result: TimeTableSlot[] = [];

    schedules.forEach((schedule) => {
      if (schedule.regionId !== topLevelRegionId) {
        return;
      }

      schedule.slots.forEach((slot) => {
        const timeslot = timeToTimeslot(date, slot.time);

        let status: TimeTableSlotStatus;
        let visitCounselId: string | null | undefined;

        if (slot.status === 'APPOINTED') {
          status = 'appointed';
          visitCounselId = slot.visitCounselId || null;
        } else if (slot.status === 'AVAILABLE') {
          status = 'available';
          visitCounselId = undefined;
        } else {
          status = 'unavailable';
          visitCounselId = undefined;
        }

        result.push({
          plannerId: schedule.plannerId,
          timeslot,
          status,
          visitCounselId,
        });
      });
    });

    return result;
  }, [schedules, date, topLevelRegionId, restrictToCustomerTimeslots]);

  return {
    slots,
    loading,
    error,
  };
}
